import * as React from "react";
import {
  acceptAllCookies,
  COOKIE_CATEGORIES,
  type CookieCategoryId,
  rejectAllCookies,
  saveCookieSelection,
  useCookieConsent,
} from "@/core/lib/cookie-consent";
import { PRIVACY_URL } from "@/core/lib/marketing-url";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Switch } from "../ui/switch";

/**
 * Per-category consent editor — the "manage preferences" half of the banner and
 * the entry point from the auth footer and Settings. Toggles are local draft
 * state until Save, so an accidental switch flick doesn't persist.
 */
export function CookiePreferencesDialog() {
  const { preferencesOpen, setPreferencesOpen, accepted } = useCookieConsent();
  const [draft, setDraft] = React.useState(accepted);

  // Re-seed the draft each time the dialog opens so it reflects what's actually
  // stored, including changes made from another surface in the same session.
  React.useEffect(() => {
    if (preferencesOpen) setDraft(accepted);
  }, [preferencesOpen, accepted]);

  const toggle = (id: CookieCategoryId, value: boolean) =>
    setDraft((prev) => ({ ...prev, [id]: value }));

  const close = () => setPreferencesOpen(false);

  return (
    <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cookie preferences</DialogTitle>
          <DialogDescription>
            Choose which cookies HR Docket may use. You can change this at any time — see our{" "}
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noreferrer"
              className="text-foreground underline underline-offset-4"
            >
              Privacy Policy
            </a>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="divide-y divide-border rounded-lg border">
          {COOKIE_CATEGORIES.map((category) => (
            <div key={category.id} className="flex items-start justify-between gap-4 p-4">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-sm font-medium text-foreground">
                  {category.label}
                  {category.locked ? (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      Always on
                    </span>
                  ) : null}
                </span>
                <span className="text-xs text-muted-foreground">{category.description}</span>
              </div>

              <Switch
                checked={category.locked ? true : draft[category.id]}
                disabled={category.locked}
                aria-label={category.label}
                onCheckedChange={(value) => toggle(category.id, value)}
              />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="sm:mr-auto"
            onClick={() => {
              rejectAllCookies();
              close();
            }}
          >
            Reject all
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              acceptAllCookies();
              close();
            }}
          >
            Accept all
          </Button>
          <Button
            type="button"
            onClick={() => {
              saveCookieSelection(draft);
              close();
            }}
          >
            Save preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
